import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const StatsSection = () => {
  const [counters, setCounters] = useState({
    experience: 0,
    experts: 0,
    farmers: 0,
    business: 0
  });

  // Detect screen size for mobile/desktop
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const stats = [
    { 
      value: counters.experience, 
      label: "Years Of Experience",
      target: 27,
      prefix: "",
      suffix: "+"
    },
    { 
      value: counters.experts, 
      label: "Experts Team",
      target: 250,
      prefix: "",
      suffix: "+"
    },
    { 
      value: counters.farmers >= 1000000 
          ? ((counters.farmers / 1000000).toFixed(1).replace(/\.0$/, "")) 
          : ((counters.farmers / 1000).toFixed(0)),
      label: "Farmers Served",
      target: 1000000,
      prefix: "",
      suffix: counters.farmers >= 1000000 ? "M+" : "K+"
    },
    { 
      value: counters.business, 
      label: "Business Vertical",
      target: 5000,
      prefix: "",
      suffix: "+"
    },
  ];

  // Create refs for counter elements
  const sectionRef = useRef(null);
  const counterRefs = useRef([]);
  const animTargetRef = useRef({ experience: 0, experts: 0, farmers: 0, business: 0 });

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    counterRefs.current = counterRefs.current.slice(0, stats.length);

    // Reset animation target
    animTargetRef.current = { experience: 0, experts: 0, farmers: 0, business: 0 };

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reset",
          once: false,
        }
      });

      const staggerTime = 0.1;
      const target = animTargetRef.current;

      tl.to(target, {
        experience: 27,
        duration: 1.5,
        ease: "power2.out",
        onUpdate: () => {
          setCounters(prev => ({ ...prev, experience: Math.floor(target.experience) }));
        }
      }, 0);

      tl.to(target, {
        experts: 250,
        duration: 1.8,
        ease: "power1.inOut",
        onUpdate: () => {
          setCounters(prev => ({ ...prev, experts: Math.floor(target.experts) }));
        }
      }, staggerTime);

      tl.to(target, {
        farmers: 1000000,
        duration: 2.2,
        ease: "power3.out",
        onUpdate: () => {
          setCounters(prev => ({ ...prev, farmers: Math.floor(target.farmers) }));
        }
      }, staggerTime * 2);

      tl.to(target, {
        business: 5000,
        duration: 2,
        ease: "elastic.out(1, 0.3)",
        onUpdate: () => {
          setCounters(prev => ({ ...prev, business: Math.floor(target.business) }));
        }
      }, staggerTime * 3);

      counterRefs.current.forEach((ref, index) => {
        if (ref) {
          tl.from(ref, {
            scale: 0.8,
            duration: 0.8,
            ease: "back.out(1.7)"
          }, staggerTime * index);
        }
      });
    }, sectionRef);

    return () => {
      ctx.revert();
    };
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Desktop View */}
      <div ref={sectionRef} className="hidden md:flex w-full h-[158px] justify-center items-center bg-gradient-to-r from-[#6B2E2F] via-[#C94B1A] to-[#FFA500]">
        <div className="flex w-full max-w-[1440px] justify-around items-center text-white">
          {stats.map((stat, index) => (
            <React.Fragment key={index}>
              <div className="flex flex-col px-6 lg:px-10 md:px-4 ">
                <h2 ref={el => counterRefs.current[index] = el} className="text-[40px] font-[LTWave] font-bold">
                  {stat.prefix}{stat.value}{stat.suffix}
                </h2>
                <p className="text-[16px] montserrat-font font-normal text-[#E4E4E4]">
                  {stat.label}
                </p>
              </div>
              {index < stats.length - 1 && (
                <div className="h-16 w-[1px] bg-white/100"></div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Mobile View */}
      <div ref={isMobile ? sectionRef : null} className="grid grid-cols-2 gap-4 px-4 py-[60px] md:hidden">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="rounded-2xl text-[28px] px-[20px] py-[17px] bg-[#F9F9FB] border border-[#D5D5D5C9] flex flex-col items-start justify-start"
          >
            <h2 
              ref={el => counterRefs.current[index] = el} 
              className="text-[#1C1C1C] text-[24px] font-[LTWave] font-bold text-left"
            >
              {stat.prefix}{stat.value}{stat.suffix}
            </h2>
            <p className="text-[#747474] text-[15px] inter-medium text-sm text-left">{stat.label}</p>
          </div>
        ))}
      </div>
    </>
  );
};

export default StatsSection;
